/** Paleta din stânga — uneltele, grupate în ordinea în care se lucrează.
 *
 *  ÎNTÂI CUTIA, APOI CE INTRĂ ÎN EA. Camera și pereții, golurile lor, piesele
 *  de structură — abia apoi mobila, electrocasnicele, obiectele sanitare și
 *  instalația electrică. E ordinea desenului, nu a catalogului: nimeni nu pune
 *  o priză înainte să existe peretele pe care se lipește.
 *
 *  NU EXISTĂ BUTOANE CARE NU FAC NIMIC. Paleta a avut patru unelte stinse
 *  («Gol de ușă», «Stâlp», «Grindă», «Nișă»), fiecare cu un tooltip care
 *  explica de ce nu se poate încă. Argumentul de atunci — «unealta există în
 *  Homestyler și cineva o va căuta» — cerea ochiului să treacă peste patru pete
 *  moarte la fiecare privire, ca să afle un lucru pe care îl afli o singură
 *  dată. Ce nu se poate desena nu se arată; când se va putea, apare.
 *
 *  ==========================================================================
 *  DOUĂ FELURI DE BUTON, ȘI AMÂNDOUĂ AU VARIANTE.
 *
 *    · UNEALTA desenează ceva ce nu stă în niciun catalog — un perete, o
 *      cameră, o ușă. Variantele ei sunt tot unelte: ușă simplă, dublă,
 *      glisantă.
 *    · FAMILIA pune un OBIECT din catalog — o canapea, un vas de WC, o priză.
 *      Variantele ei sunt felurile ei: priză, priză dublă, priză peste blat,
 *      priză de forță, priză TV.
 *
 *  DE CE FAMILII ȘI NU UN BUTON CU O LISTĂ SUB EL. Fiindcă lista era catalogul
 *  întreg — 38 de piese și 12 aparate, trei ecrane de nume prin care se caută
 *  cu degetul —, iar butonul de deasupra ei spunea doar «Mobilier». Pe familii,
 *  în pagină stau chiar lucrurile care se pun: canapea, pat, masă, dulap,
 *  bucătărie, frigider, vas, lavoar, cadă, priză, întrerupător, corp de
 *  iluminat, tablou. Restul e la un pas, în lista care se deschide sub cursor,
 *  iar butonul ține minte felul ales ultima dată: cine pune trei prize la rând
 *  le pune la fel.
 *
 *  FIECARE FEL TREBUIE SĂ FIE ÎNTR-O FAMILIE. Altfel rămân piese pe care paleta
 *  nu le poate pune deloc, iar asta nu se vede privind ecranul — deci o ține o
 *  probă (`paleta.test.tsx`), pe `felurileNepuse`.
 *  ==========================================================================
 *
 *  ELECTROCASNICELE AU GRUPA LOR, nu stau în «Bucătărie». Se așază în același
 *  front de corpuri, la aceiași 60 cm, dar nu se ALEG odată cu el: un front se
 *  desenează din corpuri identice, pe când aragazul, hota, frigiderul și
 *  mașinile sunt cinci obiecte anume, fiecare cu circuitul și racordarea lui.
 *  Sunt două gesturi, la două momente.
 *
 *  INSTALAȚIILE, la fel: o cadă și o priză se desenează ca orice piesă — un
 *  contur cu un semn în el, pus pe un perete —, dar mobila se mută cu două
 *  mâini, iar o coloană de scurgere și un șanț de doză se mută cu un deviz.
 *
 *  TOATE NUMELE VIN DIN CATALOAGE, nu din fișierul ăsta: cele ale uneltelor din
 *  `i18n`, cele ale obiectelor din catalogul lor (`numePiesa`, `numeAparat`).
 *  Un buton cu textul scris aici ar fi rămas românesc pe un ecran englezesc.
 */
import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { Unealta } from "./Panza";
import {
  felulPiesei, numePiesa, piesaSingura, PIESE_MOBILIER,
} from "../../plan/arhMobilierPlan";
import { aparatul, APARATE, marimeaSimbolului, numeAparat } from "../design/electric";
import { simbolElectric } from "../design/ElectricDesen";
import { Icoana } from "../icoane";
import { useT } from "../../../i18n";
import type { Cheie } from "../../../i18n";

/** CUTIA DE DESEN A UNUI BUTON, în pixeli — una singură pentru tot ce se pune
 *  în paletă, glife de unealtă și simboluri de obiect deopotrivă. */
const CUTIE = { w: 44, h: 32 };

/** Din ce catalog pune butonul: mobilier, obiecte sanitare sau aparate. */
type Lista = "mobilier" | "sanitare" | "electric";

/** O variantă a unui buton: ori altă UNEALTĂ, ori alt FEL din catalog. */
type Varianta = {
  /** Unealta pe care o aprinde — pentru butoanele care desenează. */
  cheie?: Unealta;
  /** Felul de piesă sau de aparat — pentru butoanele care pun obiecte. */
  fel?: string;
  /** Numele variantei-unealtă. Felurile din catalog n-au: numele lor îl dă
   *  chiar catalogul. */
  nume?: Cheie;
  glif?: ReactNode;
};

type Item = {
  cheie: Unealta;
  nume: Cheie;
  glif?: ReactNode;
  /** Din ce catalog pune, când pune un obiect. */
  lista?: Lista;
  /** VARIANTELE ACELUIAȘI LUCRU, sub un singur cap de listă.
   *
   *  Un perete drept și unul curb nu sunt două unelte, sunt același gest cu o
   *  curbură diferită. Puse alături, paleta creștea cu fiecare formă adăugată —
   *  trei feluri de cameră au făcut rândul de sus cât toată coloana. Aici stau
   *  una peste alta: butonul arată varianta aleasă ultima dată, iar colțul lui
   *  deschide lista.
   *
   *  Prima din listă e implicita. */
  variante?: Varianta[];
};

/** GLIFA UNEI UNELTE, pe grila comună.
 *
 *  Toate desenele stau în aceeași cutie și umplu cam același dreptunghi din ea
 *  — x între 4 și 40, y între 3 și 27. Nu e pedanterie: butoanele stau două pe
 *  rând, iar o glifă care umple cutia lângă una care ocupă un sfert din ea se
 *  citește ca «asta e mai importantă», ceea ce nu e adevărat niciodată.
 *
 *  Grosimea liniei e fixă, ca la simbolurile de obiect (care o țin cu
 *  `vector-effect: non-scaling-stroke`): două desene cu aceeași grosime arată
 *  ca două desene din același set. */
/** `punctat` — conturul desenat cu linie întreruptă, pentru uneltele care NU
 *  zidesc ce trasează (v. camera fără pereți). */
const G = ({ d, punctat }: { d: string; punctat?: boolean }) => (
  <svg viewBox={`0 0 ${CUTIE.w} ${CUTIE.h}`} width={CUTIE.w} height={CUTIE.h}
       fill="none" stroke="currentColor" strokeWidth="1.5"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {d.split("|").map((p, i) => (
      <path key={i} d={p} strokeDasharray={punctat ? "4 3" : undefined} />
    ))}
  </svg>
);

/** MĂRIREA LA CARE UN OBIECT UMPLE CUTIA — pixeli pe metru.
 *
 *  90%, pe latura care-l limitează: un obiect lat e oprit de lățimea cutiei,
 *  unul înalt de înălțimea ei. E mărirea maximă la care obiectul încă încape
 *  întreg. */
const umpleCutia = (w: number, h: number) =>
  Math.min((0.9 * CUTIE.w) / w, (0.9 * CUTIE.h) / h);

/** Cât de mic poate ajunge un obiect față de cutia LUI plină. Fără podea, o
 *  veioză de 26 cm desenată la scara unui pat de 2 m ar fi patru pixeli. */
const PODEA = 0.55;

/** DESENUL OBIECTULUI, nu o iconiță a lui — LA SCARA GRUPEI LUI.
 *
 *  Simbolul de planșă e ales fiindcă o canapea dreaptă și una de colț au
 *  același nume pe primele opt litere, iar la treapta de text a paletei numele
 *  se termină cam acolo. Formele nu se confundă, și sunt chiar formele care
 *  ajung pe desen: butonul arată rezultatul, nu o promisiune.
 *
 *  CE S-A SCHIMBAT E SCARA. Fiecare obiect era încadrat în cutia LUI, deci
 *  desenat cât încăpea: un scaun de 45 cm ieșea exact cât o canapea de colț de
 *  2,60 m, iar un pat înalt umplea cutia pe verticală lângă o canapea lată care
 *  o umplea pe orizontală. Coloana arăta ca o grămadă de mărimi fără normă —
 *  tocmai fiindcă mărimea NU spunea nimic.
 *
 *  Acum spune: într-o grupă, toate obiectele se desenează la ACEEAȘI scară, cea
 *  a celui mai mare din ea. Canapeaua e mare, scaunul e mic, patul e cât e —
 *  exact ca pe planșă. */
function Obiect({ fel, electric, scara }: {
  fel: string; electric?: boolean;
  /** Mărirea grupei, în pixeli pe metru. */
  scara: number;
}) {
  const a = electric ? aparatul(fel) : null;
  const p = electric ? null : felulPiesei(fel);
  if (!a && !p) return null;
  const w = a ? marimeaSimbolului(a.kind) : p!.w;
  const h = a ? marimeaSimbolului(a.kind) : p!.h;
  const k = Math.max(scara || umpleCutia(w, h), PODEA * umpleCutia(w, h));
  // Cutia, măsurată în metri la mărirea asta. Obiectul e desenat centrat în
  // origine, deci cutia se așază simetric în jurul ei.
  const cw = CUTIE.w / k, ch = CUTIE.h / k;
  return (
    // LINII MAI SUBȚIRI DECÂT PE PLANȘĂ — v. `arhMobilierPlan::g`.
    //
    // Simbolurile sunt scoase din planșă, unde o piesă are zeci de centimetri
    // pe ecran; aici au 44 × 32 px. Grosimea lor de planșă, care acolo e o
    // linie, aici acoperă desenul: frigiderul se citea ca două bare pline și
    // aragazul ca o pată neagră. Factorul ține desenul, dar îi ia din tuș.
    <svg viewBox={`${-cw / 2} ${-ch / 2} ${cw} ${ch}`}
         width={CUTIE.w} height={CUTIE.h} aria-hidden
         style={{ "--mob-gros": 0.55 } as CSSProperties}>
      {a ? simbolElectric(a.kind, w) : piesaSingura(fel, w, h)}
    </svg>
  );
}

/** O familie de obiecte: butonul care le pune, cu felurile ei. */
const familie = (nume: Cheie, lista: Lista, feluri: string[]): Item => ({
  cheie: lista === "electric" ? "aparat" : "mobila",
  nume,
  lista,
  variante: feluri.map((fel) => ({ fel })),
});

export const GRUPE: { titlu: Cheie; items: Item[] }[] = [
  {
    titlu: "paleta.grup.camera",
    items: [
      // UN SINGUR FEL DE PERETE. «Perete curb» a fost o variantă aici; a
      // ieșit: curbura e o însușire a zidului ales — bifa «Perete curbat» din
      // panou —, nu o unealtă de desenat.
      { cheie: "perete", nume: "paleta.perete",
        glif: <G d="M4 12h36|M4 20h36|M4 12v8|M40 12v8" /> },
      // ÎNTINDEREA, lângă uneltele de desenat: fără buton, singurul fel de a
      // o porni era tasta — iar o unealtă care nu se vede nicăieri și nu
      // schimbă nimic pe ecran la apăsare pare că nu există.
      // SPARGE ZID — unealta de-a-ndoaselea a peretelui: aia pune zidărie,
      // asta o dărâmă pe o bucată. Stă lângă el, fiindcă e același obiect.
      { cheie: "demoleaza", nume: "paleta.demoleaza",
        glif: <G d="M4 12h10|M4 20h10|M4 12v8|M30 12h10|M30 20h10|M40 12v8|M18 10l4 4|M22 10l-4 4|M18 18l4 4|M22 18l-4 4" /> },
      { cheie: "intinde", nume: "paleta.intinde",
        glif: <G d="M6 10h28M6 10v12M34 10v12M6 22h28|M20 4v24|M16 8l4-4 4 4|M16 24l4 4 4-4" /> },
      { cheie: "camera", nume: "paleta.camera",
        glif: <G d="M6 6h32v20H6z" />,
        variante: [
          { cheie: "camera", nume: "paleta.camera_dreptunghi",
            glif: <G d="M6 6h32v20H6z" /> },
          { cheie: "cameraL", nume: "paleta.camera_l",
            glif: <G d="M6 6h16v10h16v10H6z" /> },
          { cheie: "cameraT", nume: "paleta.camera_t",
            glif: <G d="M6 6h32v9H27v11h-9V15H6z" /> },
          { cheie: "cameraLibera", nume: "paleta.camera_libera",
            glif: <G d="M6 26 6 12l12-6 11 7 9 2-4 11z" /> },
          // CAMERA FĂRĂ PEREȚI, lângă celelalte feluri de cameră, fiindcă e un
          // fel de cameră — nu o zonă. Cerut de arhitect (22.10.2026): un duș
          // cu perimetrul lui ÎN baie, «fără să am mereu pereți acolo». Glif
          // punctat: tot un contur, dar fără zidărie pe el. Celelalte patru
          // zidesc conturul; asta îl lasă gol și felul se alege din panou.
          { cheie: "cameraFaraPereti", nume: "paleta.camera_fara_pereti",
            glif: <G d="M6 6h32v20H6z" punctat /> },
          // ZONA DE DESFUMARE, lângă celelalte feluri de cameră — acolo a
          // cerut-o arhitectul («on the left menu we should have a way to add
          // a zona de desfumare… in the room section», 14.10.2026). Glif de
          // triunghi, fiindcă așa o desenează pe planșă. Nu zidește nimic:
          // v. `model::adaugaZona`.
          { cheie: "zonaDesfumare", nume: "paleta.zona_desfumare",
            glif: <G d="M8 26h28L8 6z" /> },
        ] },
      // COTA PUSĂ DE MÂNĂ: două apăsări, de la un punct la altul. Cotele
      // planșei se socotesc singure din geometrie; asta e pentru măsurile pe
      // care planșa nu le scoate (15.10.2026).
      { cheie: "cota", nume: "paleta.cota",
        glif: <G d="M6 16h28|M6 11v10|M34 11v10|M12 16h16" /> },
    ],
  },
  {
    titlu: "paleta.grup.goluri",
    items: [
      { cheie: "usa", nume: "paleta.usa",
        glif: <G d="M12 26V7h10v19|M22 7a19 19 0 0 1 12 19|M4 26h36" />,
        variante: [
          { cheie: "usa", nume: "paleta.usa",
            glif: <G d="M12 26V7h10v19|M22 7a19 19 0 0 1 12 19|M4 26h36" /> },
          { cheie: "usaDubla", nume: "paleta.usa_dubla",
            glif: <G d="M7 26V11h7v15|M37 26V11h-7v15|M14 11a15 15 0 0 1 8 15|M30 11a15 15 0 0 0-8 15|M4 26h36" /> },
          { cheie: "usaGlisanta", nume: "paleta.usa_glisanta",
            glif: <G d="M4 26h36|M22 18h18v5H22z|M19 20.5H8|M13 16l-5 4.5 5 4.5" /> },
        ] },
      { cheie: "fereastra", nume: "paleta.fereastra",
        glif: <G d="M4 10h36v12H4z|M4 16h36|M22 10v12" /> },
    ],
  },
  {
    titlu: "paleta.grup.structura",
    items: [
      // SCĂRILE: patru forme de-a gata și una de făcut singur.
      //
      // Erau DOUĂ unelte de desenat — «dreaptă» și «în U» — care, de când forma
      // iese din laturile alese, fac exact același lucru: cu unealta de U se
      // face și o scară dreaptă, alegând laturile pe direcția de mers. Deci
      // una singură, «custom», iar formele obișnuite se pun dintr-un clic
      // (Bianca, 05.10.2026).
      { cheie: "scaraGata_dreapta", nume: "paleta.scara_gata_dreapta",
        glif: <G d="M6 27V9h32v18|M6 27h32|M13 9v18|M20 9v18|M27 9v18|M34 9v18" />,
        variante: [
          { cheie: "scaraGata_dreapta", nume: "paleta.scara_gata_dreapta",
            glif: <G d="M6 27V9h32v18|M6 27h32|M13 9v18|M20 9v18|M27 9v18|M34 9v18" /> },
          { cheie: "scaraGata_dreapta_podest",
            nume: "paleta.scara_gata_dreapta_podest",
            glif: <G d="M4 27V9h12v18|M28 27V9h12v18|M4 27h36|M9 9v18|M33 9v18|M16 9h12v18H16z" /> },
          { cheie: "scaraGata_u", nume: "paleta.scara_gata_u",
            glif: <G d="M7 21V5h30v16|M7 37V21h30v16|M7 21h30|M14 5v16|M21 5v16|M14 21v16|M21 21v16" /> },
          { cheie: "scaraGata_u_doua", nume: "paleta.scara_gata_u_doua",
            glif: <G d="M7 15V3h30v12|M7 27V15h30v12|M7 39V27h30v12|M12 3v12|M12 15v12|M12 27v12" /> },
          { cheie: "scara", nume: "paleta.scara_custom",
            glif: <G d="M6 27v-6h8v-6h8V9h8V3|M6 27h34" /> },
        ] },
      { cheie: "lift", nume: "paleta.lift",
        glif: <G d="M10 4h24v24H10z|M22 10l-5 6h10z|M22 22l-5-6h10z" /> },
      // PARAPETUL E UNEALTA DE PERETE CU ALTE IMPLICITE — 15 cm, 1,00 m
      // înălțime, `kind: parapet` — nu un al doilea fel de a desena un zid.
      { cheie: "parapet", nume: "paleta.parapet",
        glif: <G d="M4 26V7|M40 26V7|M4 11h36|M13 26V7|M22 26V7|M31 26V7" /> },
      // BALCONUL SE DESENEAZĂ CA O CAMERĂ, în afara fațadei: laturile care nu
      // se sprijină pe un zid se închid singure cu parapeți.
      { cheie: "balcon", nume: "paleta.balcon",
        glif: <G d="M4 6h36v20H4z|M4 14h36|M15 6v20|M29 6v20" /> },
    ],
  },
  {
    titlu: "paleta.grup.mobilare",
    items: [
      familie("paleta.fam.canapea", "mobilier",
              ["canapea", "canapea_colt", "fotoliu", "scaun", "taburet"]),
      familie("paleta.fam.pat", "mobilier",
              ["pat", "pat_o_persoana", "noptiera"]),
      // MASA CU SCAUNE STĂ A DOUA, nu prima: butonul din pagină poartă felul
      // dintâi al familiei, iar ăla rămâne blatul singur — e piesa de care
      // atârnă restul (masa pusă lângă un perete, biroul, masa de lucru), și
      // proba paletei o cere pe nume. Ansamblul se ia din familia deschisă,
      // ca masa rotundă.
      familie("paleta.fam.masa", "mobilier",
              ["masa", "masa_scaune", "masa_rotunda", "masuta_cafea", "masuta",
               "birou"]),
      familie("paleta.fam.depozitare", "mobilier",
              ["dulap", "biblioteca", "raft", "comoda_tv", "tv"]),
      familie("paleta.fam.bucatarie", "mobilier",
              ["corp_bucatarie", "corp_sertare", "corp_suspendat", "chiuveta"]),
      // INSULA STĂ LA MOBILARE, nu la electrocasnice: e un corp, un blat pe
      // care omul îl mărește cum vrea, nu un aparat cu circuit și racord.
      familie("paleta.fam.insula", "mobilier",
              ["insula_bucatarie", "insula_chiuveta"]),
      familie("paleta.fam.decor", "mobilier",
              ["covor", "covor_rotund", "veioza", "planta", "planta_mica"]),
    ],
  },
  {
    // MOBILAREA DE EXTERIOR, grupa ei: pe balcon nu se pune mobilă de interior
    // (Bianca, 12.10.2026), deci piesele ei nu stau amestecate cu ale camerei.
    titlu: "paleta.grup.exterior",
    items: [
      familie("paleta.fam.sezlong", "mobilier", ["sezlong"]),
      familie("paleta.fam.sezut_ext", "mobilier",
              ["fotoliu_exterior", "canapea_exterior"]),
      familie("paleta.fam.masa_ext", "mobilier",
              ["masa_exterior", "masuta_exterior"]),
      familie("paleta.fam.umbrela", "mobilier", ["umbrela", "planta_terasa"]),
    ],
  },
  {
    titlu: "paleta.grup.electrocasnice",
    items: [
      // SIMBOLUL DE PLANȘĂ, sub capul familiei: desenul nou e cel implicit
      // (12.10.2026), dar cine vrea semnul vechi îl deschide de sub el.
      familie("paleta.fam.frigider", "mobilier",
              ["frigider", "frigider_clasic"]),
      // CUPTORUL, în familia aragazului: pe planșă sunt desenate separat —
      // plita în blat, cuptorul în nișa de sub ea —, dar cine le pune le caută
      // împreună. Capul familiei rămâne aragazul, cuptorul se deschide sub el.
      familie("paleta.fam.aragaz", "mobilier",
              ["aragaz", "cuptor", "aragaz_clasic"]),
      familie("paleta.fam.hota", "mobilier", ["hota"]),
      familie("paleta.fam.masina", "mobilier",
              ["masina_spalat", "masina_vase", "masina_vase_clasic"]),
    ],
  },
  {
    titlu: "paleta.grup.sanitare",
    items: [
      familie("paleta.fam.wc", "sanitare",
              ["wc", "wc_suspendat", "wc_clasic"]),
      familie("paleta.fam.lavoar", "sanitare", ["lavoar", "lavoar_dublu"]),
      familie("paleta.fam.cada", "sanitare",
              ["cada", "cada_ovala", "dus", "dus_colt", "dus_deschis"]),
      // GHENA — puțul tehnic, 40 × 60. AICI, nu lângă scară și lift: e gaura
      // prin care urcă ȚEVILE, deci se caută împreună cu obiectele sanitare
      // care o cer. Cerut mutată. Glif: cutia hașurată, ca pe planșă.
      { cheie: "ghena", nume: "paleta.ghena",
        glif: <G d="M14 5h16v22H14z|M14 13l16-8|M14 21l16-8|M14 27l16-8" /> },
    ],
  },
  {
    titlu: "paleta.grup.electrice",
    items: [
      familie("paleta.fam.doza", "electric", ["doza"]),
      familie("paleta.fam.priza", "electric",
              ["priza", "priza_simpla", "priza_dubla", "priza_blat", "priza_forta",
               "priza_ip44"]),
      familie("paleta.fam.intrerupator", "electric",
              ["intrerupator", "intrerupator_dublu", "comutator",
               "intrerupator_general"]),
      familie("paleta.fam.iluminat", "electric",
              ["plafoniera", "spot", "aplica", "banda_led"]),
      familie("paleta.fam.curenti_slabi", "electric",
              ["interfon", "sonerie", "termostat", "priza_tv", "priza_date"]),
      familie("paleta.fam.tablou", "electric", ["tablou"]),
    ],
  },
];

/** MĂRIREA UNEI GRUPE, în pixeli pe metru: cea mai mică dintre măririle la care
 *  fiecare obiect al ei încă umple cutia. Adică mărirea la care ÎNCAP TOATE —
 *  și, prin urmare, singura la care se pot compara între ele.
 *
 *  Se ia peste TOATE variantele, nu doar peste cele arătate acum: altfel, la
 *  fiecare schimbare de variantă s-ar muta mărirea grupei și ar sări desenele
 *  din butoanele vecine, care n-au nicio treabă cu alegerea făcută. */
const scaraGrupei = (items: Item[]): number => {
  let k = Infinity;
  for (const it of items) {
    if (!it.lista) continue;
    for (const v of it.variante ?? []) {
      if (!v.fel) continue;
      const p = it.lista === "electric" ? null : felulPiesei(v.fel);
      const a = it.lista === "electric" ? aparatul(v.fel) : null;
      const w = a ? marimeaSimbolului(a.kind) : p?.w;
      const h = a ? marimeaSimbolului(a.kind) : p?.h;
      if (!w || !h) continue;
      k = Math.min(k, umpleCutia(w, h));
    }
  }
  return Number.isFinite(k) ? k : 0;
};

const SCARA = new Map<string, number>(
  GRUPE.map((g) => [g.titlu as string, scaraGrupei(g.items)]));

/** Felurile puse de paletă, pe catalog. */
const FELURI: Record<Lista, string[]> = { mobilier: [], sanitare: [], electric: [] };
for (const g of GRUPE) {
  for (const it of g.items) {
    if (!it.lista) continue;
    for (const v of it.variante ?? []) if (v.fel) FELURI[it.lista].push(v.fel);
  }
}

/** A variant list that would run past the window's bottom opens upward
 *  instead, its bottom on the button's: the last families sit at the
 *  palette's foot, and a long one there hid its last variants (sheet rows
 *  123–126 QA, an 805 px window). */
export function urcaInFereastra(el: HTMLElement, jos: number): void {
  const r = el.getBoundingClientRect();
  if (r.bottom > window.innerHeight) el.style.top = `${Math.max(0, jos - r.height)}px`;
}

/** CE NU POATE PUNE PALETA — pentru probă.
 *
 *  Un fel adăugat în catalog și uitat aici n-ar avea niciun buton, și nimic pe
 *  ecran n-ar arăta asta: lipsa unui buton dintr-o paletă cu douăzeci nu se
 *  vede. Structurile (scara, liftul) au uneltele lor în grupa «Structură», deci
 *  nu se numără. */
export const felurileNepuse = (): { mobilier: string[]; electric: string[] } => ({
  mobilier: PIESE_MOBILIER
    .filter((p) => !p.structura)
    .map((p) => p.kind)
    .filter((k) => !FELURI.mobilier.includes(k) && !FELURI.sanitare.includes(k)),
  electric: APARATE.map((a) => a.kind).filter((k) => !FELURI.electric.includes(k)),
});

export default function Paleta({
  unealta, laUnealta, piesa, laPiesa, aparat, laAparat, stransa, laStransa,
}: {
  unealta: Unealta; laUnealta: (u: Unealta) => void;
  /** Ce piesă de mobilier pune unealta «Mobilier». */
  piesa: string; laPiesa: (k: string) => void;
  /** Ce fel de aparat pune unealta «Aparat electric». */
  aparat: string; laAparat: (k: string) => void;
  stransa: boolean; laStransa: () => void;
}) {
  const t = useT();
  /** Ce variantă arată fiecare cap de listă, ca poziție în `variante`. Ține
   *  minte alegerea cât ține pagina — v. nota de la `Item.variante`. */
  const [alese, setAlese] = useState<Record<string, number>>({});
  /** Ce listă de variante e deschisă și unde — v. nota din `editor.css`. */
  const [flyout, setFlyout] = useState<
    { cheie: string; x: number; y: number; jos: number } | null>(null);
  /** GRUPELE SE STRÂNG, una câte una. Paleta are șapte grupe și peste cincizeci
   *  de unelte: cine lucrează la mobilare derulează de fiecare dată prin camere,
   *  goluri și structură ca să ajungă la ele. Cerut: «make them expandable so
   *  that we can either see just the heading or expand and see the elements».
   *  Deschise toate din pornire — paleta de azi —, iar ce strânge omul rămâne
   *  strâns cât ține sesiunea. */
  const [inchise, setInchise] = useState<Record<string, boolean>>({});

  /** Numele unei variante: al catalogului pentru obiecte, al cheii pentru
   *  unelte — un singur loc, ca butonul și lista să nu se poată numi altfel. */
  const numeVariantei = (it: Item, v: Varianta): string =>
    (v.fel
      ? (it.lista === "electric" ? numeAparat(v.fel) : numePiesa(v.fel))
      : t(v.nume ?? it.nume));

  /** Ce fel e aprins acum în catalogul grupei. */
  const felCurent = (it: Item) => (it.lista === "electric" ? aparat : piesa);

  return (
    <div className="ob-ed-paleta" onMouseLeave={() => setFlyout(null)}>
      <button className="ob-ed-strange" onClick={laStransa}
              title={t(stransa ? "paleta.desfa_titlu" : "paleta.strange_titlu")}>
        <Icoana nume={stransa ? "dreapta" : "stanga"} marime={14} />
        {!stransa && <span>{t("paleta.strange")}</span>}
      </button>
      {GRUPE.map((g) => {
        const scara = SCARA.get(g.titlu as string) ?? 0;
        return (
          <section key={g.titlu}>
            <button type="button" className="ob-ed-cap"
                    aria-expanded={!inchise[g.titlu as string]}
                    onClick={() => setInchise((x) => ({
                      ...x, [g.titlu as string]: !x[g.titlu as string] }))}>
              <Icoana nume={inchise[g.titlu as string] ? "dreapta" : "jos"}
                      marime={11} />
              <span>{t(g.titlu)}</span>
            </button>
            <div className="ob-ed-unelte"
                 hidden={!!inchise[g.titlu as string]}>
              {g.items.map((it, i) => {
                const id = `${g.titlu}:${it.nume}`;
                // BUTONUL ARATĂ VARIANTA ALEASĂ ULTIMA DATĂ, nu prima din
                // listă: cine pune prize la rând le pune de obicei la fel, și
                // n-are de ce să treacă prin listă de fiecare dată.
                const v = it.variante?.[alese[id] ?? 0];
                // APRINS înseamnă altceva la o unealtă și la o familie: unealta
                // e aprinsă cât e activă, familia numai când felul de sub cursor
                // e chiar unul de-al ei — altfel s-ar aprinde toate cele șase
                // familii de mobilier deodată, fiindcă toate pun cu aceeași
                // unealtă.
                const activa = it.lista
                  ? unealta === it.cheie
                    && (it.variante ?? []).some((x) => x.fel === felCurent(it))
                  : it.variante
                    ? it.variante.some((x) => x.cheie === unealta)
                    : unealta === it.cheie;
                /** Aprinde varianta: unealta ei, iar pentru obiecte și felul. */
                const pune = (x: Varianta) => {
                  if (x.fel) {
                    if (it.lista === "electric") laAparat(x.fel); else laPiesa(x.fel);
                    laUnealta(it.cheie);
                    return;
                  }
                  laUnealta(x.cheie ?? it.cheie);
                };
                const areLista = (it.variante?.length ?? 0) > 1;
                return (
                  <div key={i} className="ob-ed-slot"
                       onMouseEnter={(e) => areLista && setFlyout({
                         cheie: id,
                         x: e.currentTarget.getBoundingClientRect().right - 6,
                         y: e.currentTarget.getBoundingClientRect().top,
                         jos: e.currentTarget.getBoundingClientRect().bottom,
                       })}>
                    <button
                      className={`ob-ed-unealta${activa ? " on" : ""}`}
                      title={v ? numeVariantei(it, v) : t(it.nume)}
                      onClick={() => (v ? pune(v) : laUnealta(it.cheie))}>
                      {/* Familia arată OBIECTUL ales, unealta glifa ei. */}
                      <span className="ob-ed-desen">
                        {it.lista && v?.fel
                          ? <Obiect fel={v.fel} scara={scara}
                                    electric={it.lista === "electric"} />
                          : (v?.glif ?? it.glif)}
                      </span>
                      <span>{it.lista && v ? numeVariantei(it, v) : t(it.nume)}</span>
                    </button>
                    {areLista && (
                      <>
                        {/* Colțul care spune «mai sunt». Lista se deschide la
                            plimbarea cursorului peste buton — fără un al doilea
                            clic doar ca s-o vezi. */}
                        <i className="ob-ed-colt" aria-hidden />
                        {flyout?.cheie === id && (
                          <div className="ob-ed-variante"
                               ref={(el) => { if (el) urcaInFereastra(el, flyout.jos); }}
                               style={{ left: flyout.x, top: flyout.y }}>
                            {it.variante!.map((x, j) => (
                              <button key={x.fel ?? x.cheie ?? j}
                                      className={`ob-ed-varianta${
                                        (x.fel
                                          ? x.fel === felCurent(it) && unealta === it.cheie
                                          : unealta === x.cheie) ? " on" : ""}`}
                                      onClick={() => {
                                        setAlese((a) => ({ ...a, [id]: j }));
                                        pune(x);
                                        setFlyout(null);
                                      }}>
                                <span className="ob-ed-desen">
                                  {x.fel
                                    ? <Obiect fel={x.fel} scara={scara}
                                              electric={it.lista === "electric"} />
                                    : x.glif}
                                </span>
                                <span>{numeVariantei(it, x)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
